import { createAnalyticsMethodFactory } from './customerio-factory';
import { debugLog } from './debug';
import { getEnvironmentName, isTelemetryEnabled } from './env';
import { getUsername, type DescopeUser } from './utils';

// Type definitions for Customer.io CDP Analytics object
interface CustomerIOAnalytics {
  initialize?: boolean;
  invoked?: boolean;
  methods: string[];
  factory: (method: string) => (...args: unknown[]) => CustomerIOAnalytics;
  push: (args: unknown[]) => void;
  load: (key: string, options?: CustomerIOLoadOptions) => void;
  identify: (id: string, attributes?: Record<string, unknown>) => void;
  reset: () => void;
  page: () => void;
  _writeKey?: string;
  _loadOptions?: CustomerIOLoadOptions;
  // Analytics.js reads `_cdn` off the global at top-level load to set its
  // webpack publicPath for lazy chunks. Set it so the core bundle + chunks
  // load through the tunnel instead of the hardcoded cdp.customer.io default.
  _cdn?: string;
  SNIPPET_VERSION?: string;
  [key: string]: unknown; // Index signature for dynamic method assignment
}

interface CustomerIOIntegrationOptions {
  apiHost?: string;
  anonymousInApp?: boolean;
  [key: string]: unknown;
}

/**
 * Customer.io CDN settings. Mirrors the shape returned by
 * GET /v1/projects/{writeKey}/settings. `metrics.host` and the integration
 * `apiHost`s decide where Analytics.js sends events AND its internal
 * performance metrics (POST /v1/m) — both default to cdp.customer.io when
 * absent, which is why we always provide them explicitly.
 */
interface CustomerIOCdnSettings {
  integrations?: Record<string, CustomerIOIntegrationOptions>;
  metrics?: { sampleRate?: number; host?: string };
  remotePlugins?: unknown;
  [key: string]: unknown;
}

interface CustomerIOLoadOptions {
  integrations?: Record<string, CustomerIOIntegrationOptions>;
  cdnURL?: string;
  cdnSettings?: CustomerIOCdnSettings;
}

const CUSTOMERIO_TUNNEL_SDK_PREFIX = '/cio-sdk';
const CUSTOMERIO_TUNNEL_API_PREFIX = '/cio-api';

interface CustomerIOEndpoints {
  cdnUrl: string;
  apiHost: string;
  snippetSrc: string;
  protocol: 'http' | 'https';
}

function buildSnippetPath(writeKey: string): string {
  return `${CUSTOMERIO_TUNNEL_SDK_PREFIX}/snippet/${writeKey}/analytics.min.js`;
}

function resolveCustomerIOEndpoints(writeKey: string): CustomerIOEndpoints {
  const apiBase = (
    (import.meta.env.VITE_API_BASE as string | undefined) || ''
  ).replace(/\/+$/, '');
  const tunnelOrigin = import.meta.env.DEV
    ? window.location.origin
    : apiBase || window.location.origin;
  const tunnelHost = new URL(tunnelOrigin).host;
  const snippetPath = buildSnippetPath(writeKey);
  const snippetSrc = import.meta.env.DEV
    ? snippetPath
    : `${tunnelOrigin}${snippetPath}`;

  return {
    // SDK fetches /v1/projects/{writeKey}/settings relative to cdnURL.
    cdnUrl: `${tunnelOrigin}${CUSTOMERIO_TUNNEL_API_PREFIX}`,
    apiHost: `${tunnelHost}${CUSTOMERIO_TUNNEL_API_PREFIX}/v1`,
    snippetSrc,
    protocol: import.meta.env.DEV ? 'http' : 'https',
  };
}

declare global {
  interface Window {
    cioanalytics?: CustomerIOAnalytics | unknown[];
  }
}

const CUSTOMERIO_SNIPPET_MAX_DEV_ATTEMPTS = 4;

function insertCustomerIOSnippetScript(src: string, attempt = 1): void {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.setAttribute('data-global-customerio-analytics-key', 'cioanalytics');
  script.src = src;

  script.onerror = () => {
    if (import.meta.env.DEV && attempt < CUSTOMERIO_SNIPPET_MAX_DEV_ATTEMPTS) {
      debugLog(
        `[Customer.io] Snippet load failed (attempt ${attempt}); retrying after backend startup`
      );
      window.setTimeout(
        () => insertCustomerIOSnippetScript(src, attempt + 1),
        1000 * attempt
      );
      return;
    }
    debugLog(
      '[Customer.io] Failed to load analytics snippet; tracking dropped for this session.'
    );
  };

  script.onload = () => {
    debugLog('[Customer.io] Script loaded successfully');
  };

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    const head = document.head || document.getElementsByTagName('head')[0];
    head?.appendChild(script);
  }
}

let isInitialized = false;

/**
 * Initialize Customer.io CDP Analytics SDK
 * Replicates the official Customer.io snippet pattern programmatically
 */
export async function initCustomerIO(): Promise<void> {
  debugLog('[Customer.io] initCustomerIO called');

  if (isInitialized || typeof window === 'undefined') {
    debugLog(
      '[Customer.io] Early return: isInitialized=',
      isInitialized,
      'window=',
      typeof window
    );
    return;
  }

  const writeKey = import.meta.env.VITE_CUSTOMERIO_WRITE_KEY as
    | string
    | undefined;
  const endpoints = writeKey ? resolveCustomerIOEndpoints(writeKey) : null;
  const cdnUrl = endpoints?.cdnUrl ?? '';
  const apiHost = endpoints?.apiHost ?? '';
  const snippetSrc = endpoints?.snippetSrc ?? '';
  const protocol = endpoints?.protocol ?? 'https';
  // Customer.io requires both writeKey and telemetry to be enabled
  // This is consistent with Mixpanel and ensures the SDK can actually be used
  // (AuthenticatedTelemetry only renders when telemetry is enabled)
  // It is required because we need the identity to be set for in-app messages to work
  const enabled = !!writeKey && isTelemetryEnabled();

  if (!enabled) {
    if (!writeKey) {
      console.warn(
        '[Customer.io] VITE_CUSTOMERIO_WRITE_KEY not set. Customer.io will not be initialized.'
      );
    } else if (!isTelemetryEnabled()) {
      debugLog(
        '[Customer.io] Telemetry is disabled. Customer.io will not be initialized.'
      );
    }
    return;
  }

  // Claim initialization synchronously, before the first `await` below. Without
  // this, concurrent initCustomerIO() calls would all clear the `isInitialized`
  // check and race through the async settings fetch. (`analytics.invoked` also
  // guards re-entry, but only via the misleading "Snippet included twice" path.)
  isInitialized = true;

  // Initialize cioanalytics as array/queue if it doesn't exist
  const analytics = (window.cioanalytics =
    window.cioanalytics || []) as CustomerIOAnalytics;

  // Check if already initialized to prevent double loading
  if (analytics.initialize) {
    return;
  }

  // Check if snippet was included twice
  if (analytics.invoked) {
    if (window.console && console.error) {
      console.error('[Customer.io] Snippet included twice.');
    }
    return;
  }

  // Mark as invoked
  analytics.invoked = true;

  // Define all available methods
  analytics.methods = [
    'trackSubmit',
    'trackClick',
    'trackLink',
    'trackForm',
    'pageview',
    'identify',
    'reset',
    'group',
    'track',
    'ready',
    'alias',
    'debug',
    'page',
    'once',
    'off',
    'on',
    'addSourceMiddleware',
    'addIntegrationMiddleware',
    'setAnonymousId',
    'addDestinationMiddleware',
  ];

  analytics.factory = createAnalyticsMethodFactory(analytics);

  // Create method wrappers for all methods
  for (let i = 0; i < analytics.methods.length; i++) {
    const key = analytics.methods[i];
    analytics[key] = analytics.factory(key);
  }

  // Load function to inject the script
  analytics.load = function (key: string, e?: CustomerIOLoadOptions): void {
    debugLog('[Customer.io] analytics.load called with key=', key);

    if (typeof document === 'undefined') {
      debugLog('[Customer.io] Early return: document is undefined');
      return;
    }

    analytics._writeKey = key;
    analytics._loadOptions = e;
    // Pin the webpack publicPath (read from `_cdn` at top-level bundle load) so
    // Analytics.js fetches its core + lazy chunks from {cdn}/v1/analytics-js/*
    // via the tunnel. Without this it falls back to cdp.customer.io and the
    // "Failed to load Analytics.js" / performance-metrics errors fire.
    analytics._cdn = e?.cdnURL ?? cdnUrl;
    insertCustomerIOSnippetScript(snippetSrc);

    debugLog('[Customer.io] Script element created for', snippetSrc);
    debugLog('[Customer.io] Analytics object', analytics);
  };

  analytics.SNIPPET_VERSION = '4.15.3';

  debugLog(
    '[Customer.io] About to call analytics.load with writeKey=',
    writeKey
  );

  const integrations: Record<string, CustomerIOIntegrationOptions> = {
    'Customer.io Data Pipelines': { apiHost, protocol },
    'Segment.io': { apiHost, protocol },
  };

  // Resolve CDN settings ourselves and pass them inline as `cdnSettings`.
  // This makes Analytics.js skip its own settings fetch, so it can never fall
  // back to the hardcoded cdp.customer.io default for either event delivery or
  // its internal performance metrics (POST /v1/m). We also force the tunnel
  // apiHost/metrics host regardless of what upstream returns.
  const cdnSettings = await resolveCdnSettings(
    cdnUrl,
    writeKey,
    apiHost,
    protocol
  );
  analytics.load(writeKey, { cdnURL: cdnUrl, integrations, cdnSettings });
  analytics.page();
  debugLog('[Customer.io] Initialization complete');
}

/** Force every integration + the metrics reporter onto the tunnel host. */
function pinSettingsToTunnel(
  settings: CustomerIOCdnSettings,
  apiHost: string,
  protocol: 'http' | 'https'
): CustomerIOCdnSettings {
  const integrations = settings.integrations ?? {};
  for (const integration of Object.values(integrations)) {
    // Independent checks (matching the backend rewriteSettingsBody): an
    // integration may carry only one of apiHost/protocol, and both must be
    // pinned to the tunnel so the SDK can never route around it.
    if (typeof integration.apiHost === 'string') {
      integration.apiHost = apiHost;
    }
    if (typeof integration.protocol === 'string') {
      integration.protocol = protocol;
    }
  }
  return {
    ...settings,
    integrations,
    // sampleRate 0 disables remote performance metrics entirely (no POST /v1/m).
    metrics: { ...settings.metrics, host: apiHost, sampleRate: 0 },
  };
}

async function resolveCdnSettings(
  cdnUrl: string,
  writeKey: string,
  apiHost: string,
  protocol: 'http' | 'https'
): Promise<CustomerIOCdnSettings> {
  const fallback: CustomerIOCdnSettings = {
    integrations: {
      'Customer.io Data Pipelines': { apiHost, apiKey: writeKey, protocol },
    },
    metrics: { host: apiHost, sampleRate: 0 },
  };

  try {
    const res = await fetch(`${cdnUrl}/v1/projects/${writeKey}/settings`, {
      credentials: 'omit',
    });
    if (!res.ok) {
      debugLog('[Customer.io] Settings fetch non-2xx; using fallback settings');
      return fallback;
    }
    const settings = (await res.json()) as CustomerIOCdnSettings;
    return pinSettingsToTunnel(settings, apiHost, protocol);
  } catch (error) {
    debugLog('[Customer.io] Settings fetch failed; using fallback', error);
    return fallback;
  }
}

/**
 * Identify a user in Customer.io
 * @param id - User ID (required)
 * @param email - User email (optional)
 * @param user - Descope user object (optional, used to extract username)
 */
export function customerIOIdentify(
  id?: string,
  email?: string,
  user?: DescopeUser | null | undefined
): void {
  if (!id || typeof window === 'undefined') return;

  // Build attributes object
  const customerIOAttributes: Record<string, unknown> = {};

  if (email) {
    customerIOAttributes.email = email;
  }

  const userName = getUsername(user);
  if (userName) {
    customerIOAttributes.name = userName;
  }

  // Include environment in attributes for consistency
  const envName = getEnvironmentName();
  const attrs = {
    ...customerIOAttributes,
    environment: envName,
  };

  const analytics = window.cioanalytics as CustomerIOAnalytics | undefined;
  if (!analytics) {
    debugLog('[Customer.io] SDK not available for user identification');
    return;
  }

  try {
    // Always use push to queue the identify command
    // This prevents recursive loops that can occur when the SDK's push method
    // processes the queue and calls identify, which might still be the factory function
    analytics.push(['identify', id, attrs]);
  } catch (error) {
    debugLog('[Customer.io] Failed to identify user:', error);
  }
}

/**
 * Reset Customer.io identity (call on logout)
 */
export function customerIOReset(): void {
  if (typeof window === 'undefined') return;

  const analytics = window.cioanalytics as CustomerIOAnalytics | undefined;
  if (!analytics) {
    debugLog('[Customer.io] SDK not available for identity reset');
    return;
  }

  try {
    // Always use push to queue the reset command
    // This prevents recursive loops and follows the Customer.io snippet pattern
    analytics.push(['reset']);
  } catch (error) {
    debugLog('[Customer.io] Failed to reset identity:', error);
  }
}
